import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import WidgetCard from "../_shared/WidgetCard";
import ViewAllButton from "../_shared/ViewAllButton";
import ViewAllModal from "../modals/ViewAllModal";
import { TeacherDatum, VIEW_ALL_PAGE_SIZE } from "../data";
import { getTopTeachers } from "shared/services/headOfficeService";
import { mapTopTeachers } from "../apiMappers";
import type { ViewAllPage } from "../modals/ViewAllModal";
import styles from "./style.module.scss";

interface TopTeachersProps {
  teachers: TeacherDatum[];
  /** reporting window so the modal fetches the same range */
  win: { from: string; to: string };
  /** header + view-all copy overrides for campus-scoped reuse */
  title?: string;
  subtitle?: string;
  viewAllTitle?: string;
  viewAllSubtitle?: string;
  /** sublabels under the metric columns — each renders only when given, and
   *  the second column only exists when `quizLabel` is set */
  completedLabel?: string;
  quizLabel?: string;
  /** one header over the metric column, shown once above the rows;
   *  empty hides the header row */
  columnHeader?: string;
  /** unit after the assigned count ("0 / 9 books"); empty drops it */
  valueSuffix?: string;
  /** when given, rows become clickable and open the teacher drill-down.
   *  `rank` is 1-based across pages — only rank 1 earns a certificate. */
  onOpenTeacher?: (teacher: TeacherDatum, rank: number) => void;
  /** override the paginated fetch (campus reuse paginates static data) */
  fetchPage?: (page: number) => Promise<ViewAllPage>;
}

const TeacherIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1897A6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10 12 5 2 10l10 5 10-5z" />
    <path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
  </svg>
);

export default function TopTeachers({
  teachers,
  win,
  title = "Top Teachers",
  subtitle = "Ranked by students' book completion and assignment performance",
  viewAllTitle = "Top Teachers",
  viewAllSubtitle = "Network-wide — all teachers",
  completedLabel,
  columnHeader = "Completed / Assigned",
  quizLabel,
  valueSuffix = "books",
  onOpenTeacher,
  fetchPage: fetchPageProp,
}: TopTeachersProps) {
  const [open, setOpen] = useState(false);
  const pageRef = useRef(1); // page currently rendered in the modal (for ranks)

  const header = columnHeader ? (
    <div className={styles.headerRow}>
      <span className={styles.headerLabel}>{columnHeader}</span>
    </div>
  ) : null;

  const renderRows = (list: TeacherDatum[], offset = 0) =>
    list.map((t, i) => (
      <div
        className={classNames(styles.row, onOpenTeacher && styles.rowClickable)}
        key={`${t.name}-${offset + i}`}
        role={onOpenTeacher ? "button" : undefined}
        onClick={
          onOpenTeacher ? () => onOpenTeacher(t, offset + i + 1) : undefined
        }
      >
        <span className={styles.rank}>{offset + i + 1}</span>
        <div className={styles.info}>
          <div className={styles.name} data-ho-blur="teacher">
            {t.name}
          </div>
          <div className={styles.campus} data-ho-blur="campus">
            {t.campus}
          </div>
        </div>
        <div className={styles.metric}>
          <div className={styles.completed}>
            {t.completed}
            <span className={styles.assigned}>
              {" "}/ {t.assigned}
              {valueSuffix && ` ${valueSuffix}`}
            </span>
          </div>
          {completedLabel && (
            <div className={styles.metricLabel}>{completedLabel}</div>
          )}
        </div>
        {quizLabel && (
          <div className={styles.metric}>
            <div className={styles.quizVal}>{t.quiz}%</div>
            <div className={styles.metricLabel}>{quizLabel}</div>
          </div>
        )}
      </div>
    ));

  const fetchPage = useCallback(
    async (page: number) => {
      pageRef.current = page;
      const res = await getTopTeachers({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page });
      const data = res?.data?.data ?? {};
      const p = data?.pagination ?? {};
      return {
        rows: mapTopTeachers(data),
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [win.from, win.to],
  );

  const effectiveFetchPage = fetchPageProp ?? fetchPage;

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      icon={TeacherIcon}
      right={<ViewAllButton onClick={() => setOpen(true)} />}
      flush
    >
      {header}
      {renderRows(teachers.slice(0, 4))}

      {open && (
        <ViewAllModal
          title={viewAllTitle}
          subtitle={viewAllSubtitle}
          onClose={() => setOpen(false)}
          fetchPage={effectiveFetchPage}
          renderContent={(rows) => (
            <>
              {header}
              {renderRows(rows as TeacherDatum[], (pageRef.current - 1) * VIEW_ALL_PAGE_SIZE)}
            </>
          )}
        />
      )}
    </WidgetCard>
  );
}
